<template>
  <div
    v-if="isVisible"
    :class="$style.successMessage"
    :style="{ backgroundColor: backgroundColor, boxShadow: boxShadow }"
  >
    {{ message }} {{ emoji }} "{{ taskName }}"
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { TimeDeviationCategory } from "../types/enums";

const props = defineProps<{
  taskName: string;
  accuracy: number;
  duration?: number;
}>();

const isVisible = ref(false);
let timeoutId: number | null = null;

// Determine message and styling based on accuracy
const message = computed(() => {
  if (props.accuracy <= TimeDeviationCategory.PERFECT * 100) {
    return "Perfect timing!";
  } else if (props.accuracy <= TimeDeviationCategory.GREAT * 100) {
    return "Great timing!";
  } else if (props.accuracy <= TimeDeviationCategory.GOOD * 100) {
    return "Well done - on time!";
  } else {
    return "Task completed!";
  }
});

const emoji = computed(() => {
  if (props.accuracy <= TimeDeviationCategory.PERFECT * 100) {
    return "🎯";
  } else if (props.accuracy <= TimeDeviationCategory.GREAT * 100) {
    return "🎉";
  } else if (props.accuracy <= TimeDeviationCategory.GOOD * 100) {
    return "👍";
  } else {
    return "✅";
  }
});

const backgroundColor = computed(() => {
  if (props.accuracy <= TimeDeviationCategory.GREAT * 100) {
    return "#00b894"; // Green
  } else if (props.accuracy <= TimeDeviationCategory.GOOD * 100) {
    return "#fdcb6e"; // Yellow
  } else {
    return "#00b894"; // Fallback green
  }
});

const boxShadow = computed(() => {
  return `0 4px 12px ${backgroundColor.value}40`;
});

onMounted(() => {
  // Show the message
  isVisible.value = true;

  // Hide after 3 seconds
  timeoutId = window.setTimeout(() => {
    isVisible.value = false;
  }, 3000);
});

onUnmounted(() => {
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
});
</script>

<style module>
.successMessage {
  position: fixed;
  top: 20px;
  right: 20px;
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 14px;
  z-index: 10000;
  transform: translateX(100%);
  transition: transform 0.3s ease;
  max-width: 300px;
  word-wrap: break-word;
  animation: slideIn 0.3s ease forwards;
}

@keyframes slideIn {
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(0);
  }
}

.successMessage:global(.fade-out) {
  animation: slideOut 0.3s ease forwards;
}

@keyframes slideOut {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(100%);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .successMessage {
    top: 10px;
    right: 10px;
    left: 10px;
    max-width: none;
    font-size: 13px;
    padding: 10px 16px;
  }
}
</style>
